import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  TextField,
} from '@material-ui/core';
import { Info as InfoIcon, Close, FilterList } from '@material-ui/icons';
import { Lookup } from '@components/Lookup';
import { flatMap, orderBy } from 'lodash';
import { t } from 'ttag';
import useStyles from './styles';
import Header from '@components/Header/Header';
import ServiceNavigationBar from '@components/Menu/ServiceNavigationBar';
import { Button, IconButton, FilterRequire } from '@coolremote/shared';
import BacnetDeviceTableRow from './DevicesTableRow';
import BacnetUnitTableRow from './UnitTableRow';
import useBacNet from '@hooks/useBacNet';
import { useStoreState, useStoreActions } from '@models/RootStore';
import AddEditBacnetUiTemplateDialog from '@components/AddEditBacnetUiTemplateDialog';
import { useGetSiteBacnetUiTemplates } from '@hooks/useBacnetUiTemplatesApi';
import AddBacnetDevicesDialog from './AddBacnetDevicesDialog';
import Search from './../../../svgComponents/MenuSearch';
import { useConfirmation } from '@coolremote/shared';
import { smartStructureConvert } from '@utils/StructureConversionUtils';
import BacNetMapping from '../../BacNetMapping';
import TemplateManagement from '../TemplateManagment';
import { BacnetDevice } from 'coolremote-sdk';

const STORAGE_KEY_EXPANDED = 'bacnet_expanded_devices';

const STATUS_TYPES = {
  BOUND: 'bound',
  NOT_BOUND: 'notBound',
};

const getUnitStatus = (unit) => {
  const { isEngaged } = unit;
  return isEngaged ? STATUS_TYPES.BOUND : STATUS_TYPES.NOT_BOUND;
};

const STATUS_LABELS = {
  [STATUS_TYPES.BOUND]: () => t`Bound`,
  [STATUS_TYPES.NOT_BOUND]: () => t`Not Bound`,
};

const STATUS_COLORS = {
  [STATUS_TYPES.BOUND]: '#5FBA82',
  [STATUS_TYPES.NOT_BOUND]: '#000',
};

/**
 * BacnetDeviceList component renders a list of BACnet devices for a selected site.
 */
const BacnetDeviceList = (props) => {
  const classes = useStyles();
  const {
    siteId,
    bacnetDeviceId: selectedBacnetDeviceId,
    bacnetLineId: selectedBacnetLineId,
  } = useStoreState((state) => state.selections.selections);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);
  const allDevices = useStoreState((state) => state.devices.allDevices);
  const siteHasBacnetSupport = Object.values(allDevices).some((device) => device.site == siteId && device.itcVersion);
  const confirm = useConfirmation();

  const [searchTerm, setSearchTerm] = useState('');
  const [initialParamTemplate, setInitialParamTemplate] = useState(null);
  const [isBacnetUiTemplateDialogShown, setIsBacnetUiTemplateDialogShown] = useState(false);
  const [isBacnetParamTemplateDialogShown, setIsBacnetParamTemplateDialogShown] = useState(false);
  const [isAddDevicesDialogOpenMode, setIsAddDevicesDialogOpenMode] = useState('');
  // Lives here, not in AddBacnetDevicesDialog, so devices found in one discovery session
  // are still shown the next time the dialog opens instead of starting from an empty list.
  const [discoveredBacnetDevices, setDiscoveredBacnetDevices] = useState([]);
  const [isRefetchRequested, setIsRefetchRequested] = useState(false);
  const [showTemplateManagement, setShowTemplateManagement] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isAddDeviceButtonLoading, setIsAddDevicesButtonLoading] = useState(false);

  // Expand/collapse state - persisted in localStorage
  const [expandedDevices, setExpandedDevices] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_EXPANDED);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const toggleDeviceExpand = useCallback((deviceId) => {
    setExpandedDevices((prev) => {
      const next = { ...prev, [deviceId]: !prev[deviceId] };
      try { localStorage.setItem(STORAGE_KEY_EXPANDED, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  const {
    getSiteBacNetParameterTemplates,
    siteBacNetParameterTemplates,
    siteBacNetUnits,
    getSiteBacNetUnits,
    siteBacNetDevices,
    getSiteBacNetDevices,
    assignBacNetParameterTemplate,
    unassignBacNetParameterTemplate,
    updateBacNetUnit,
    assignBacNetUiTemplate,
    unassignBacNetUiTemplate,
  } = useBacNet();

  const fetchData = useCallback(async (silent = false, ignoreDialogs = false) => {
    if (!siteId) {
      return;
    }
    if (!ignoreDialogs && (isBacnetParamTemplateDialogShown || isBacnetUiTemplateDialogShown)) {
      return;
    }
    if (isRefetchRequested) {
      setIsRefetchRequested(false);
    }
    if (!silent) {
      setIsDataLoading(true);
    }
    // Use allSettled so one failure doesn't block others
    await Promise.allSettled([
      getSiteBacNetDevices(siteId, silent),
      getSiteBacNetUnits(siteId, silent),
      getSiteBacNetParameterTemplates(siteId, silent),
    ]);
    if (!silent) {
      setIsDataLoading(false);
    }
  }, [siteId, isBacnetUiTemplateDialogShown, isBacnetParamTemplateDialogShown, isRefetchRequested]);

  useEffect(() => {
    if (siteId) {
      getSiteBacNetParameterTemplates(siteId);
    }
  }, [siteId]);

  const handleConnectionChange = async (bacnetUnitId, isEngaged) => {
    await updateBacNetUnit({
      id: bacnetUnitId,
      data: { isEngaged },
    });
  };

  const handleParamTemplateAssign = async (tempId, bacnetUnitId) => {
    await assignBacNetParameterTemplate(tempId, bacnetUnitId);
  };

  const handleParamTemplateUnassign = async (tempId, bacnetUnitId) => {
    await unassignBacNetParameterTemplate(tempId, bacnetUnitId);
  };

  const handleUiTemplateAssign = async (tempId, bacnetUnitId) => {
    const uiTemplate = uiTemplates.find((template) => template.id === tempId);
    await assignBacNetUiTemplate(uiTemplate.id, uiTemplate.paramTemplate, bacnetUnitId);
  };

  const handleUiTemplateUnassign = async (tempId, bacnetUnitId) => {
    await unassignBacNetUiTemplate(tempId, bacnetUnitId);
  };

  // Marks `id` as the row the user is currently acting on: it becomes the highlighted/scrolled-to
  // row, and collapses any "newly added" highlight down to just this item.
  const markManipulated = (id) => {
    setLastManipulatedDeviceId(id);
    setHighlightedDeviceIds(new Set());
  };

  const goToUiTemplateMapping = (defaultParamTemplate, id, rowSelectedParamTemplate) => {
    markManipulated(id);
    setDefaultParamTemplateForUiTemplate(defaultParamTemplate);
    setInitialParamTemplate(rowSelectedParamTemplate);
    setIsBacnetUiTemplateDialogShown(true);
  };

  const goToParamTemplateMapping = (BACnetDeviceId, rowSelectedParamTemplate) => {
    markManipulated(BACnetDeviceId);
    setInitialParamTemplate(rowSelectedParamTemplate);
    setIsBacnetParamTemplateDialogShown(true);
  };

  useEffect(() => {
    setIsBacnetParamTemplateDialogShown(false);
    setIsBacnetUiTemplateDialogShown(false);
    setShowTemplateManagement(false);
    setInitialParamTemplate(null);
    if (!siteId) {
      return;
    }
    fetchData(false, true);
  }, [siteId]);

  const [lastManipulatedDeviceId, setLastManipulatedDeviceId] = useState(null);
  // Devices just added via the Add BACnet Device dialog, highlighted until the user acts on any row.
  const [highlightedDeviceIds, setHighlightedDeviceIds] = useState(() => new Set());
  const highlightedRowRef = useRef(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (highlightedRowRef.current && tableContainerRef.current) {
      requestAnimationFrame(() => {
        const rowElement = highlightedRowRef.current;
        const tableContainer = tableContainerRef.current;
        const rowTop = rowElement.offsetTop;
        const rowHeight = rowElement.offsetHeight;
        const containerHeight = tableContainer.clientHeight;
        const targetScroll = rowTop - (containerHeight / 2) + (rowHeight / 2);
        tableContainer.scrollTop = targetScroll;
      });
    }
  }, [lastManipulatedDeviceId, isBacnetParamTemplateDialogShown, isBacnetUiTemplateDialogShown]);

  const [defaultParamTemplateForUiTemplate, setDefaultParamTemplateForUiTemplate] = useState(null);

  const { data: uiTemplates, refetch: refetchUiTemplates } =
    useGetSiteBacnetUiTemplates(siteId);

  // Group units by their parent bacnet device
  const deviceUnitsMap = useMemo(() => {
    if (!siteBacNetUnits) return {};
    const map = {};
    Object.values(siteBacNetUnits).forEach((unit) => {
      const deviceId = unit.bacnetDevice || unit.id;
      const parentDevice = siteBacNetDevices?.[deviceId];
      if (!parentDevice || parentDevice.isUserSelected !== true) {
        return;
      }
      // Use bacnetDevice field if available, fall back to unit.id (1:1 mapping)
      if (!map[deviceId]) map[deviceId] = [];
      map[deviceId].push(unit);
    });
    return map;
  }, [siteBacNetUnits, siteBacNetDevices]);

  const { vendors, usedTemplates } = useMemo(() => {
    if (!siteBacNetUnits) return { vendors: {} };
    const vendorMap = {};
    const usedTemplates = {};
    Object.values(siteBacNetUnits).forEach((unit) => {
      const deviceId = unit.bacnetDevice || unit.id;
      const parentDevice = siteBacNetDevices?.[deviceId];
      if (!parentDevice || parentDevice.isUserSelected !== true) {
        return;
      }
      if (unit.vendorId && unit.vendorName) {
        vendorMap[unit.vendorId] = unit.vendorName;
      }
      if (unit.paramTemplate) {
        usedTemplates[unit.paramTemplate] = true;
      }
      if (unit.uiTemplate) {
        usedTemplates[unit.uiTemplate] = true;
      }
    });
    return { vendors: vendorMap, usedTemplates };
  }, [siteBacNetUnits, siteBacNetDevices]);

  // Sort and filter devices
  const sortedBacnetDevices = useMemo(() => {
    return orderBy(
      Object.values(siteBacNetDevices).filter((entry) => {
        if (entry.isUserSelected !== true) {
          return false;
        }
        if (selectedBacnetDeviceId && entry.device !== selectedBacnetDeviceId) {
          return false;
        }
        if (selectedBacnetLineId && entry.line != selectedBacnetLineId) {
          return false;
        }

        // Status filter - check if any unit under this device matches the filter
        if (statusFilter.length > 0) {
          const units = deviceUnitsMap[entry.id] || [];
          const caDeviceIsConnected = allDevices?.[entry.device]?.isConnected;
          const hasMatchingUnit = units.some((unit) => {
            const status = getUnitStatus(unit, caDeviceIsConnected);
            return statusFilter.includes(status);
          });
          // Also check device-level connectivity for devices with no units
          if (!hasMatchingUnit && units.length === 0) {
            const deviceConnected = caDeviceIsConnected && entry.isConnected;
            const deviceStatus = deviceConnected ? STATUS_TYPES.CONNECTED : STATUS_TYPES.DISCONNECTED;
            if (!statusFilter.includes(deviceStatus)) return false;
          } else if (!hasMatchingUnit) {
            return false;
          }
        }

        if (searchTerm) {
          const searchTermLower = searchTerm.toLowerCase();
          const entryValues = flatMap(
            Object.values(entry).filter((v) => !!v)
          ).map((value) => value.toString().toLowerCase());
          // Also search through units of this device
          const units = deviceUnitsMap[entry.id] || [];
          const unitValues = units.flatMap((unit) =>
            Object.values(unit).filter((v) => !!v).map((v) => v.toString().toLowerCase())
          );
          const allValues = [...entryValues, ...unitValues];
          if (!allValues.some((value) => value.includes(searchTermLower))) {
            return false;
          }
        }
        return true;
      }),
      ['name'],
      ['asc']
    );
  }, [
    siteBacNetDevices,
    deviceUnitsMap,
    selectedBacnetDeviceId,
    selectedBacnetLineId,
    searchTerm,
    statusFilter,
    allDevices,
  ]);

  // Summary counts — only units that belong to a device in the current (filtered)
  // list, sourced from deviceUnitsMap (same as the table rows), so the "App Units"
  // total matches the "BACnet Devices" total shown beside it rather than counting
  // every unit on the site.
  const allUnits = useMemo(
    () => sortedBacnetDevices.flatMap((device) => deviceUnitsMap[device.id] || []),
    [sortedBacnetDevices, deviceUnitsMap]
  );

  const connectedBacnetDevicesCount = useMemo(() => {
    return sortedBacnetDevices.filter((device) => device.isConnected).length;
  }, [sortedBacnetDevices]);

  const disconnectedBacnetDevicesCount = useMemo(() => {
    return sortedBacnetDevices.filter((device) => !device.isConnected).length;
  }, [sortedBacnetDevices]);

  const unitStatusCounts = useMemo(() => {
    const counts = { connected: 0, bound: 0, boundDisconnected: 0, disconnected: 0 };
    allUnits.forEach((unit) => {
      const caDevice = allDevices?.[unit.device];
      const status = getUnitStatus(unit, caDevice?.isConnected);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [allUnits, allDevices]);


  const handleEditUiTemplateDialogClose = async (template) => {
    const device = siteBacNetUnits[lastManipulatedDeviceId];
    const allowedParamTemplatesMap = smartStructureConvert(device.compatibleParamTemplates).object;
    setIsBacnetUiTemplateDialogShown(false);

    if (!template || !allowedParamTemplatesMap[template.paramTemplate]) {
      refetchUiTemplates();
      return;
    }
    try {
      await handleUiTemplateAssignment(device, template);
    } catch (error) {
      console.error('Error updating templates:', error);
    } finally {
      refetchUiTemplates();
    }
  };

  const handleParamTemplateClose = async (newParamTemplateId, selectedParamTemplateId) => {

    setIsBacnetParamTemplateDialogShown(false);
    setInitialParamTemplate(null);
    getSiteBacNetParameterTemplates(siteId);

    if (!newParamTemplateId || newParamTemplateId.includes('new_')) {
      return;
    }

    if (newParamTemplateId === selectedParamTemplateId) {
      await getSiteBacNetUnits(siteId, true);
      return;
    }

    try {
      if (!selectedParamTemplateId) {
        await handleParamTemplateAssign(
          newParamTemplateId,
          lastManipulatedDeviceId
        );
        return;
      }

      const userConfirmed = await confirm({
        title: t`Replace Template`,
        bodyMessage: t`Do you want to replace the existing template?`,
        confirmText: t`Replace`,
        cancelText: t`Cancel`,
      });

      if (userConfirmed) {
        await assignBacNetParameterTemplate(
          newParamTemplateId,
          lastManipulatedDeviceId
        );
      }
    } catch (error) {
      console.error('Error assigning parameter template:', error);
    } finally {
      refetchUiTemplates();
      fetchData();
    }
  };

  const handleUiTemplateAssignment = async (device, uiTemplate) => {
    if (!device.uiTemplate) {
      await assignBacNetUiTemplate(uiTemplate.id, uiTemplate.paramTemplate, lastManipulatedDeviceId);
      return;
    }
    if (device.uiTemplate === uiTemplate.id) {
      await assignBacNetUiTemplate(uiTemplate.id, uiTemplate.paramTemplate, lastManipulatedDeviceId);
      return;
    }

    const userResponse = await confirm({
      title: t`Replace Control UI`,
      bodyMessage: t`Do you want to replace the existing Control UI?`,
      confirmText: t`Replace`,
      cancelText: t`Cancel`,
    });

    if (userResponse) {
      await assignBacNetUiTemplate(uiTemplate.id, uiTemplate.paramTemplate, lastManipulatedDeviceId);
    }
  };

  const onDeleteBacknetDevice = () => {
    fetchData(true);
  };

  const searchComponent = (
    <TextField
      placeholder={t`Search...`}
      value={searchTerm}
      onChange={(event) => setSearchTerm(event.target.value)}
      InputProps={{
        disableUnderline: true,
        classes: { root: classes.inputRoot },
        endAdornment: !searchTerm ? (
          <Search />
        ) : (
          <IconButton
            onClick={() => setSearchTerm('')}
            className={classes.closeIconStyle}
          >
            <Close />
          </IconButton>
        ),
      }}
    />
  );

  const statusFilterOptions = Object.values(STATUS_TYPES).map((value) => STATUS_LABELS[value]());
  const appliedStatusFilterLabels = statusFilter.map((value) => STATUS_LABELS[value]());

  const handleStatusFilterClick = (event) => {
    setStatusFilterAnchor(event.currentTarget);
  };

  const handleStatusFilterApply = (selectedLabels) => {
    const selected = Object.values(STATUS_TYPES).filter((value) =>
      selectedLabels.includes(STATUS_LABELS[value]())
    );
    setStatusFilter(selected);
    setStatusFilterAnchor(null);
  };

  const getLineCellValue = (device) => {
    const { line } = device;
    if (isNaN(line)) {
      return '';
    }
    if (line === 0) {
      return 'IP';
    }
    return `L${line}`;
  };

  const tableHeaders = [
    '', // expand arrow
    <span key="device-name">{t`Device Name`}<br />{t`(ID)`}</span>,
    <span key="vendor-name">{t`Vendor Name`}<br />{t`(ID)`}</span>,
    t`Model`,
    t`MAC Addr`,
    <span key="ca-device-sn">{t`CA Device SN`}<br />{t`(Line)`}</span>,
    t`App Unit`,
    t`Mapping`,
    t`Control UI`,
    t`Universal`,
    null, // Status - rendered separately with filter
    ' ', // actions
  ];

  const getMode = (hasOld, hasNew)=> {
    if (hasNew) {
      return 'discover';
    }
    if (hasOld) {
      return 'results';
    }
    return null;
  }

  const handleAddDeviceClick = async () => {
    setIsAddDevicesButtonLoading(true);
    try {
      //todo: replace with acttual data from BE. 
      const {hasOld = false, hasNew = false} = await BacnetDevice.discoverBacnetDevices({siteId}) ?? {};
      const mode = getMode(hasOld, hasNew);
       setIsAddDevicesDialogOpenMode(mode);
       if(!mode){
          addMessage({ message: t`Failed to start discovering BACnet devices` });
          console.error({hasOld, hasNew})
       }
    } catch (err) {
      console.error('Failed to getDiscoverDeviceMode:', err);
      addMessage({ message: err?.message || t`Failed to discover BACnet devices` });
    } finally {
       setIsAddDevicesButtonLoading(false);

    }
  };

  const renderPageContent = () => {
    if (!siteId) {
      return <FilterRequire type={t`site`} />;
    }

    if (isDataLoading) {
      return (
        <Paper elevation={0} className={classes.paperTableContainer}>
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        </Paper>
      );
    }

    if (!siteHasBacnetSupport) {
      return (
        <FilterRequire
          text={t`Connect CoolMaster EDGE device to the site to start BACnet devices discovery.`}
        />
      );
    }

    const handleTemplateManagementClick = () => {
      setShowTemplateManagement(true);
    };
    
    const handleAddDialogClose = (shouldRefetch, addedDeviceIds) => {
      setIsAddDevicesDialogOpenMode('');
      if (shouldRefetch) {
        if (addedDeviceIds?.length) {
          setHighlightedDeviceIds(new Set(addedDeviceIds));
          setLastManipulatedDeviceId(addedDeviceIds[0]);
          // Added devices are no longer "newly discovered" - drop them from the cache so a
          // future reopen doesn't offer to add them again.
          setDiscoveredBacnetDevices((prev) => prev.filter((device) => !addedDeviceIds.includes(device.id)));
        }
        fetchData();
      }
    }

    return (
      <>
        <Paper elevation={0} className={classes.paperTableContainer}>
          <div className={classes.pageHeader}>
            <Typography>
              <span className={classes.bold}>{t`BACnet Devices`}: </span>{(() => {
                const deviceParts = [
                  connectedBacnetDevicesCount > 0 && `${connectedBacnetDevicesCount} ${t`Connected`}`,
                  disconnectedBacnetDevicesCount > 0 && `${disconnectedBacnetDevicesCount} ${t`Disconnected`}`,
                ].filter(Boolean);
                return `${sortedBacnetDevices.length} ${t`Total`}${deviceParts.length ? ` (${deviceParts.join(', ')})` : ''}`;
              })()}{' | '}
              <span className={classes.bold}>{t`App Units`}: </span>{(() => {
                const unitParts = [
                  unitStatusCounts.connected > 0 && `${unitStatusCounts.connected} ${t`Connected`}`,
                  unitStatusCounts.bound > 0 && `${unitStatusCounts.bound} ${t`Bound to App`}`,
                  unitStatusCounts.boundDisconnected > 0 && `${unitStatusCounts.boundDisconnected} ${t`Bound Disconnected`}`,
                  unitStatusCounts.disconnected > 0 && `${unitStatusCounts.disconnected} ${t`Disconnected`}`,
                ].filter(Boolean);
                return `${allUnits.length} ${t`Total`}${unitParts.length ? ` (${unitParts.join(', ')})` : ''}`;
              })()}
            </Typography>

            <Button
              white
              disableRipple
              disableElevation
              onClick={handleTemplateManagementClick}
              variant="contained"
              className={classes.templateManagementButton}
            >
              {t`Template Management`}
            </Button>

            <Button
            variant="contained"
            disabled={isAddDeviceButtonLoading}
            onClick={handleAddDeviceClick}>
              {t`+ Add BACnet Devices`}
            </Button>

          </div>
          <TableContainer ref={tableContainerRef} className={classes.tableContainer}>
            <Table
              stickyHeader
              className={classes.table}
              aria-label="customized table"
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((entry, index) => {
                    if (entry === null) {
                      // Status column with filter
                      return (
                        <TableCell
                          key={`tableHeaders-${index}`}
                          classes={{ root: classes.tableHeadCell }}
                          align="left"
                          onClick={handleStatusFilterClick}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={classes.headContainer}>
                            {t`Status`}
                            <FilterList
                              className={`${classes.filterStyle} ${statusFilter.length > 0 ? classes.blueFilter : ''}`}
                            />
                          </div>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={`tableHeaders-${index}`}
                        classes={{ root: classes.tableHeadCell }}
                        align="left"
                        style={index === 0 ? { width: 40, padding: '0 5px' } : undefined}
                      >
                        {entry}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBacnetDevices?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} align="center">
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <InfoIcon style={{ fontSize: 50 }} />
                        <Typography variant="h6">{t`No data available`}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedBacnetDevices.filter((device) => (deviceUnitsMap[device.id] || []).length > 0).map((device) => {
                    const units = deviceUnitsMap[device.id] || [];
                    const isExpanded = !!expandedDevices[device.id];
                    const serial = allDevices[device.device]?.serial;
                    const lineValue = getLineCellValue(device);
                    const unitCount = units.length;
                    const boundCount = units.filter((u) => u.isEngaged).length;
                    const caDeviceIsConnected = allDevices?.[device.device]?.isConnected;

                    const singleUnit = units.length === 1 ? units[0] : null;
                    const singleUnitStatus = singleUnit
                      ? getUnitStatus(singleUnit, caDeviceIsConnected)
                      : null;

                    const onMenuClick = (id) => {
                      markManipulated(id);
                    };

                    const highlightRow = lastManipulatedDeviceId === singleUnit?.id
                      || lastManipulatedDeviceId === device?.id
                      || highlightedDeviceIds.has(device?.id)

                    return (
                      <React.Fragment key={device.id}>
                        <BacnetDeviceTableRow
                          ref={
                            (singleUnit && lastManipulatedDeviceId === singleUnit.id) || lastManipulatedDeviceId === device?.id
                              ? highlightedRowRef
                              : null
                          }
                          highlightRow={highlightRow}
                          device={device}
                          unitCount={unitCount}
                          boundCount={boundCount}
                          expanded={isExpanded}
                          onToggleExpand={() => toggleDeviceExpand(device.id)}
                          caDeviceSerial={serial || ''}
                          caDeviceLine={lineValue}
                          isDisconnected={!caDeviceIsConnected || !device.isConnected}
                          onUnitCreated={() => fetchData(true)}
                          onDeviceDeleted={() => fetchData(true)}
                          singleUnit={singleUnit}
                          singleUnitStatus={singleUnitStatus}
                          handleConnectionChange={handleConnectionChange}
                          handleParamTemplateAssign={handleParamTemplateAssign}
                          handleParamTemplateUnassign={handleParamTemplateUnassign}
                          handleUiTemplateAssign={handleUiTemplateAssign}
                          handleUiTemplateUnassign={handleUiTemplateUnassign}
                          goToUiTemplateMapping={goToUiTemplateMapping}
                          goToParamTemplateMapping={goToParamTemplateMapping}
                          onDeleteUnit={onDeleteBacknetDevice}
                          updateBacNetUnit={updateBacNetUnit}
                          siteBacNetParameterTemplates={siteBacNetParameterTemplates}
                          uiTemplates={uiTemplates}
                          statusColors={STATUS_COLORS}
                          statusLabels={STATUS_LABELS}
                          onMenuClick={onMenuClick}
                        />
                        {isExpanded && !singleUnit && units.map((unit) => {
                          const { canUpdate } = unit?.permissions || {};
                          const unitStatus = getUnitStatus(unit, caDeviceIsConnected);
                          return (
                            <BacnetUnitTableRow
                              ref={
                                lastManipulatedDeviceId === unit.id
                                  ? highlightedRowRef
                                  : null
                              }
                              highlightRow={lastManipulatedDeviceId === unit.id}
                              key={unit.id}
                              bacnetUnit={unit}
                              canUpdate={canUpdate}
                              unitStatus={unitStatus}
                              handleConnectionChange={handleConnectionChange}
                              handleParamTemplateAssign={handleParamTemplateAssign}
                              handleParamTemplateUnassign={handleParamTemplateUnassign}
                              handleUiTemplateAssign={handleUiTemplateAssign}
                              handleUiTemplateUnassign={handleUiTemplateUnassign}
                              goToUiTemplateMapping={goToUiTemplateMapping}
                              goToParamTemplateMapping={goToParamTemplateMapping}
                              onDeleteUnit={onDeleteBacknetDevice}
                              updateBacNetUnit={updateBacNetUnit}
                              siteBacNetParameterTemplates={siteBacNetParameterTemplates}
                              uiTemplates={uiTemplates}
                              siteId={siteId}
                              statusColors={STATUS_COLORS}
                              statusLabels={STATUS_LABELS}
                              onMenuClick={onMenuClick}
                            />
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {isBacnetUiTemplateDialogShown ? (
          <AddEditBacnetUiTemplateDialog
            open={isBacnetUiTemplateDialogShown}
            onClose={handleEditUiTemplateDialogClose}
            defaultParamTemplate={defaultParamTemplateForUiTemplate}
            initialParamTemplate={initialParamTemplate}
          />
        ) : null}
        {statusFilterAnchor && (
          <Lookup
            filtersList={statusFilterOptions}
            appliedFilters={appliedStatusFilterLabels}
            onApply={handleStatusFilterApply}
            lookupAnchor={statusFilterAnchor}
            onClose={() => setStatusFilterAnchor(null)}
            tableHasFilters={statusFilter.length > 0}
            clearAllFilters={() => { setStatusFilter([]); setStatusFilterAnchor(null); }}
          />
        )}
        {Boolean(isAddDevicesDialogOpenMode) && (
          <AddBacnetDevicesDialog
            mode={isAddDevicesDialogOpenMode}
            onClose={handleAddDialogClose}
            siteId={siteId}
            newDevices={discoveredBacnetDevices}
            onNewDevicesChange={setDiscoveredBacnetDevices}
          />
        )}
      </>
    );
  };

  const handleTemplateManagementClose = () => {
    setShowTemplateManagement(false);
    getSiteBacNetUnits(siteId, true);
    getSiteBacNetDevices(siteId, true);
    getSiteBacNetParameterTemplates(siteId, true);
    refetchUiTemplates();
  };

  const renderContent = () => {

    if (isBacnetParamTemplateDialogShown) {
      return (
        <BacNetMapping
          bacnetUnitId={lastManipulatedDeviceId}
          siteId={siteId}
          handleClose={handleParamTemplateClose}
        />
      );
    }

    if (showTemplateManagement && siteId && siteHasBacnetSupport) {
      return (
        <TemplateManagement onClose={handleTemplateManagementClose} vendorsMap={vendors} usedTemplates={usedTemplates} />
      );
    }

    return renderPageContent();

  };

  return (
    <div className={classes.view}>
      <ServiceNavigationBar {...props} />
      <div className={classes.contentArea}>
        <Header
          customGeneralNames={{ site: t`Select Site` }}
          hideSystemSelection
          hideUnitSelection
          screenTitle="siteBacNetUnits"
          showBacnetDevicesMenu={isBacnetParamTemplateDialogShown ? false : true}
          showBacnetLinesMenu={isBacnetParamTemplateDialogShown ? false : true}
          searchComponent={searchComponent}
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default BacnetDeviceList;
